import type { FunnelOutcomeExitCount } from "@repo/app-types/api/funnel";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import {
  findFunnelOutcomeColor,
  formatFunnelOutcomeLabel,
  formatFunnelPreciseShare,
  formatFunnelShare,
  funnelColorClassNames,
  sortFunnelExitsByCategory,
} from "./funnelFormatting.js";

export interface FunnelMomentShareBarProps {
  momentName: string;
  /** Of the stage's interactions, those with the moment. */
  share: number;
  /** Of those, the ones that dropped off; null in the last stage. */
  dropOffRate: number | null;
  /** The drop-offs by outcome, drawn in their outcomes' colors. */
  droppedOffByOutcome: readonly FunnelOutcomeExitCount[];
  /** The stage's interactions in prose, from `formatFunnelStageInteractions`. */
  stageInteractions: string;
  /** The slideover's full-width bar, without the trailing percentage. */
  isExpanded?: boolean;
}

/** The moment's share of the stage, split into continued, then drop-offs by outcome in the flow's order. */
export const FunnelMomentShareBar: FC<FunnelMomentShareBarProps> = (props) => {
  const {
    momentName,
    share,
    dropOffRate,
    droppedOffByOutcome,
    stageInteractions,
    isExpanded = false,
  } = props;
  const droppedOff = droppedOffByOutcome.reduce(
    (sum, exit) => sum + exit.interactions,
    0,
  );
  // Each outcome's part of the moment's interactions.
  const segments = sortFunnelExitsByCategory(droppedOffByOutcome).map(
    (exit) => ({
      key: `${exit.category ?? ""}:${exit.label ?? ""}`,
      label: formatFunnelOutcomeLabel(exit.label),
      color: findFunnelOutcomeColor(exit.category),
      rate:
        droppedOff > 0
          ? ((dropOffRate ?? 0) * exit.interactions) / droppedOff
          : 0,
    }),
  );
  const dropOffDescription = segments
    .map(
      (segment) =>
        `${formatFunnelPreciseShare(share * segment.rate)} ${segment.label}`,
    )
    .join(", ");
  const description =
    dropOffRate === null
      ? `${formatFunnelShare(share)} of ${stageInteractions} had this moment.`
      : `${formatFunnelShare(share)} of ${stageInteractions} had this moment: ${formatFunnelPreciseShare(share * dropOffRate)} dropped off afterward${dropOffDescription ? ` (${dropOffDescription})` : ""} and ${formatFunnelPreciseShare(share * (1 - dropOffRate))} continued.`;

  return (
    <span
      className={twMerge(
        "flex shrink-0 items-center gap-3",
        isExpanded ? "w-full" : "w-44 sm:w-64",
      )}
      title={description}
    >
      <span
        role="img"
        aria-label={`${momentName}. ${description}`}
        className={twMerge(
          "flex flex-1 overflow-hidden rounded-full bg-border-strong/40",
          isExpanded ? "h-3" : "h-2",
        )}
      >
        <span
          className="flex h-full overflow-hidden rounded-full"
          style={{ width: `${share * 100}%` }}
        >
          <span
            className={twMerge(
              "h-full flex-1",
              funnelColorClassNames.continued.background,
            )}
          />
          {segments.map((segment) => (
            <span
              key={segment.key}
              className={twMerge(
                "h-full",
                funnelColorClassNames[segment.color].background,
              )}
              style={{ width: `${segment.rate * 100}%` }}
            />
          ))}
        </span>
      </span>
      {!isExpanded && (
        <span className="w-9 text-right text-xs text-muted-foreground tabular-nums">
          {formatFunnelShare(share)}
        </span>
      )}
    </span>
  );
};
